import React from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, Stack, CircularProgress,
  Divider,
} from '@mui/material';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import Colors from '../../components/ui/Colors';
import { submitOnboarding, getPublicOnboardingStatus, resendActivation } from '../../utilityFunctions/OnboardingUtil';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POLL_MS = 3000;

// PUBLIC (no-login) pre-activation surface, mounted at /onboarding/self-serve.
// The payment redirect lands here with ?token=<onboardingToken>. It collects
// org name + admin email, submits, and then shows a "check your email" panel
// that polls provisioning status until the activation email is on its way.
export default function OnboardingStartPage(props) {
  const search = (props.location && props.location.search) || window.location.search || '';
  const onboardingToken = new URLSearchParams(search).get('token');

  const [phase, setPhase] = React.useState('form'); // form | sent
  const [orgName, setOrgName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [adminName, setAdminName] = React.useState('');
  const [errors, setErrors] = React.useState({});
  const [submitError, setSubmitError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const [pollStatus, setPollStatus] = React.useState(null);
  const [resendMsg, setResendMsg] = React.useState('');
  const [resending, setResending] = React.useState(false);

  // --- "Check your email" provisioning poll --------------------------------
  React.useEffect(() => {
    if (phase !== 'sent' || !onboardingToken) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      const res = await getPublicOnboardingStatus(onboardingToken);
      if (cancelled) return;
      if (res.ok && res.data) {
        setPollStatus(res.data);
        if (res.data.status === 'FAILED') return; // terminal — stop polling
      }
      timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [phase, onboardingToken]);

  const validate = () => {
    const next = {};
    if (!orgName.trim()) next.orgName = 'Required';
    if (!email.trim()) next.email = 'Required';
    else if (!EMAIL_RE.test(email.trim())) next.email = 'Enter a valid email address';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError('');
    if (!validate()) return;
    setBusy(true);
    const res = await submitOnboarding(onboardingToken, {
      orgName: orgName.trim(),
      email: email.trim().toLowerCase(),
      adminName: adminName.trim() || undefined,
    });
    setBusy(false);
    if (!res.ok) {
      setSubmitError(res.error || 'We could not start your setup. Please try again or contact support.');
      return;
    }
    setPhase('sent');
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    await resendActivation(email.trim().toLowerCase());
    setResending(false);
    // Always a generic confirmation — never reveal whether the email exists.
    setResendMsg(`If ${email.trim().toLowerCase()} is on file, a new activation link is on its way.`);
  };

  const shell = (children) => (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 620, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Box component="img" src="/images/logo_datalove.png" alt="Datalove" sx={{ height: 40, width: 'auto' }} />
      </Stack>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 2 }}>{children}</Paper>
    </Box>
  );

  // --- Missing / invalid link ----------------------------------------------
  if (!onboardingToken) {
    return shell(
      <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
        <ErrorOutlineRoundedIcon sx={{ fontSize: 48, color: Colors.third }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: Colors.primary }}>
          Invalid or missing onboarding link
        </Typography>
        <Typography color="text.secondary">
          This page needs a valid onboarding link. Please use the link from your welcome email,
          or contact support if you think this is a mistake.
        </Typography>
      </Stack>,
    );
  }

  // --- "Check your email" confirmation --------------------------------------
  if (phase === 'sent') {
    const failed = pollStatus && pollStatus.status === 'FAILED';
    return shell(
      <Stack spacing={2.5} sx={{ py: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <MarkEmailReadRoundedIcon sx={{ fontSize: 40, color: Colors.secondary }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: Colors.primary }}>
            Check your email
          </Typography>
        </Stack>

        {failed ? (
          <Alert severity="error">
            {(pollStatus && pollStatus.error) || 'Something went wrong while setting up your workspace.'}
            {' '}Please contact support and we&apos;ll get you sorted.
          </Alert>
        ) : (
          <>
            <Typography color="text.secondary">
              We&apos;re setting up your workspace. We&apos;ve sent an activation link to{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {email.trim().toLowerCase()}
              </Box>
              {' '}— click it to set your password and finish signing in.
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                {pollStatus && pollStatus.status
                  ? `Provisioning your workspace… (${pollStatus.status})`
                  : 'Preparing your workspace…'}
              </Typography>
            </Stack>
          </>
        )}

        <Divider />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Didn&apos;t get the email? Check your spam folder, or resend it.
          </Typography>
          <Button
            variant="outlined"
            onClick={handleResend}
            disabled={resending}
            startIcon={resending ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderColor: Colors.primary, color: Colors.primary }}
          >
            {resending ? 'Sending…' : 'Resend activation email'}
          </Button>
          {resendMsg && <Alert severity="info" sx={{ mt: 1.5 }}>{resendMsg}</Alert>}
        </Box>
      </Stack>,
    );
  }

  // --- Details form ---------------------------------------------------------
  return shell(
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: Colors.primary }}>
          Set up your workspace
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Tell us a bit about your organization and where to send your admin activation link.
        </Typography>
      </Box>

      {submitError && <Alert severity="error">{submitError}</Alert>}

      <TextField
        label="Organization name"
        value={orgName}
        onChange={(e) => setOrgName(e.target.value)}
        error={!!errors.orgName}
        helperText={errors.orgName || ' '}
        disabled={busy}
        fullWidth
        required
      />
      <TextField
        label="Admin email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!errors.email}
        helperText={errors.email || 'The activation link is sent here.'}
        disabled={busy}
        fullWidth
        required
      />
      <TextField
        label="Admin name (optional)"
        value={adminName}
        onChange={(e) => setAdminName(e.target.value)}
        disabled={busy}
        fullWidth
      />

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ alignSelf: 'flex-start', backgroundColor: Colors.primary, '&:hover': { backgroundColor: Colors.primary } }}
      >
        {busy ? 'Submitting…' : 'Continue'}
      </Button>
    </Stack>,
  );
}
